import { useForm, FieldValues } from 'react-hook-form';
import { useState, useEffect, useCallback } from 'react';
import { ResponseStoreFrontProductSearchFacetOption } from '../interfaces/data-contracts';

type UseFilterProps = {
  facets: any[];
  watchedFields: string[];
  onSubmit: (data: FieldValues) => void;
};

export const useFilter = ({ facets, watchedFields, onSubmit }: UseFilterProps) => {
  const { register, handleSubmit, setValue, getValues, watch, reset } = useForm();
  const [selectedFilterCount, setSelectedFilterCount] = useState(0);

  const watchedValues = watchedFields.reduce((acc: any, field: string) => {
    acc[field] = watch(field);
    return acc;
  }, {});

  // count checkbox selections from watched values
  useEffect(() => {
    const totalSelections = Object.values(watchedValues).reduce((acc: number, current: any) => {
      // Ensure `current` is iterable; default to an empty array if not
      const iterableCurrent = Array.isArray(current) ? current : [];
      return acc + new Set(iterableCurrent).size;
    }, 0);
    setSelectedFilterCount(totalSelections);
  }, [watchedValues]);

  // prevents inf. loop
  const updateFacetValues = useCallback(() => {
    facets?.forEach((facet) => {
      facet.options?.forEach((option: ResponseStoreFrontProductSearchFacetOption) => {
        const currentValues = Array.isArray(getValues(facet.code)) ? getValues(facet.code) : [];
        if (option.isSelected && !currentValues.includes(option.code)) {
          setValue(`${facet.code}`, [...currentValues, option.code]);
        }
      });
    });
  }, [facets, setValue, getValues]);

  useEffect(() => {
    updateFacetValues();
  }, [facets, updateFacetValues]);

  const onSubmitHandler = handleSubmit((data: FieldValues) => {
    onSubmit(data);
  });

  const resetAndSubmit = handleSubmit((data: FieldValues) => {
    reset();
    const updatedValues = getValues(); // new reset values
    handleSubmit(onSubmit)(updatedValues as any);
  });

  return {
    register,
    getValues,
    setValue,
    reset,
    resetAndSubmit,
    watchedValues,
    selectedFilterCount,
    onSubmitHandler,
  };
};
